import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft, CheckCircle, Copy, ExternalLink, Shield,
  CreditCard, Settings, Bell, RefreshCw, BookOpen, ArrowRight, Users, Gift
} from 'lucide-react';

const CopyableUrl = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="mt-2 flex items-center gap-2 bg-gray-950 border border-gray-700 rounded-lg p-3">
      <code className="text-green-400 text-sm flex-1 break-all">{url}</code>
      <Button size="sm" variant="outline" onClick={handleCopy} className="flex-shrink-0 border-gray-600" data-testid="copy-ipn-url-btn">
        {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </Button>
    </div>
  );
};

const steps = [
  {
    num: 1,
    title: 'Produkte in Digistore24 anlegen',
    icon: <CreditCard className="w-5 h-5" />,
    content: (
      <>
        <p className="text-gray-300 mb-4">
          Loggen Sie sich bei <a href="https://www.digistore24.com" target="_blank" rel="noreferrer" className="text-yellow-500 hover:underline">digistore24.com</a> ein und erstellen Sie folgende Abo-Produkte:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Paket</th>
                <th className="text-left py-3 px-4 text-yellow-500 font-semibold">Enthaltene Kurse</th>
                <th className="text-right py-3 px-4 text-yellow-500 font-semibold">Empf. Preis</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-3 px-4 font-medium text-white">Basis-Paket</td>
                <td className="py-3 px-4">DGUV V1, VDE 0105-100, Leitern & Gerüste, Baustellen-Recht</td>
                <td className="py-3 px-4 text-right text-green-400 font-mono">29,90€/Mo</td>
              </tr>
              <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-3 px-4 font-medium text-white">Add-On PV</td>
                <td className="py-3 px-4">DC/AC Montage, PSA Absturz, VNB-Anmeldung</td>
                <td className="py-3 px-4 text-right text-green-400 font-mono">19,90€/Mo</td>
              </tr>
              <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-3 px-4 font-medium text-white">Add-On Smart Home</td>
                <td className="py-3 px-4">KNX, Loxone, Shelly</td>
                <td className="py-3 px-4 text-right text-green-400 font-mono">19,90€/Mo</td>
              </tr>
              <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-3 px-4 font-medium text-white">Add-On Büro</td>
                <td className="py-3 px-4">Ergonomie, Archivierung, Kommunikation</td>
                <td className="py-3 px-4 text-right text-green-400 font-mono">14,90€/Mo</td>
              </tr>
              <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-3 px-4 font-medium text-white">Add-On KI</td>
                <td className="py-3 px-4">ChatGPT, Normen-Recherche, Dokumentation</td>
                <td className="py-3 px-4 text-right text-green-400 font-mono">14,90€/Mo</td>
              </tr>
              <tr className="hover:bg-gray-800/50">
                <td className="py-3 px-4 font-medium text-yellow-400">Komplett-Paket</td>
                <td className="py-3 px-4">Alle 16 Kurse</td>
                <td className="py-3 px-4 text-right text-yellow-400 font-mono font-bold">79,90€/Mo</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <p className="text-sm text-gray-400"><strong className="text-white">So geht's:</strong></p>
          <ol className="text-sm text-gray-400 mt-2 space-y-1 list-decimal list-inside">
            <li>Konto &rarr; Produkte &rarr; <strong>Neues Produkt</strong></li>
            <li>Produkttyp: <strong>"Abo/Membership"</strong></li>
            <li>Preis und Abrechnungszyklus (monatlich) eintragen</li>
            <li>Speichern und <strong>Produkt-ID notieren</strong> (z.B. <code className="bg-gray-700 px-1 rounded">12345</code>)</li>
          </ol>
        </div>
      </>
    )
  },
  {
    num: 2,
    title: 'Produkt-IDs mitteilen',
    icon: <Settings className="w-5 h-5" />,
    content: (
      <>
        <p className="text-gray-300 mb-4">
          Nachdem Sie die Produkte erstellt haben, teilen Sie mir die IDs mit. Ich hinterlege sie in der Plattform.
        </p>
        <div className="bg-gray-950 rounded-lg p-4 border border-gray-700 font-mono text-sm space-y-1">
          <div className="text-gray-500"># Ihre Digistore24 Produkt-IDs</div>
          <div><span className="text-yellow-500">DIGISTORE_PRODUCT_BASIS</span><span className="text-gray-500">=</span><span className="text-green-400">&lt;Basis Produkt-ID&gt;</span></div>
          <div><span className="text-yellow-500">DIGISTORE_PRODUCT_PV</span><span className="text-gray-500">=</span><span className="text-green-400">&lt;PV Produkt-ID&gt;</span></div>
          <div><span className="text-yellow-500">DIGISTORE_PRODUCT_SMARTHOME</span><span className="text-gray-500">=</span><span className="text-green-400">&lt;Smart Home Produkt-ID&gt;</span></div>
          <div><span className="text-yellow-500">DIGISTORE_PRODUCT_BUERO</span><span className="text-gray-500">=</span><span className="text-green-400">&lt;Büro Produkt-ID&gt;</span></div>
          <div><span className="text-yellow-500">DIGISTORE_PRODUCT_KI</span><span className="text-gray-500">=</span><span className="text-green-400">&lt;KI Produkt-ID&gt;</span></div>
          <div><span className="text-yellow-500">DIGISTORE_PRODUCT_KOMPLETT</span><span className="text-gray-500">=</span><span className="text-green-400">&lt;Komplett Produkt-ID&gt;</span></div>
        </div>
      </>
    )
  },
  {
    num: 3,
    title: 'IPN-Webhook einrichten',
    icon: <Bell className="w-5 h-5" />,
    content: (
      <>
        <p className="text-gray-300 mb-4">
          Der IPN-Webhook sorgt dafür, dass Abos <strong className="text-white">automatisch aktiviert</strong> werden, wenn jemand kauft.
        </p>
        <ol className="space-y-3 text-sm text-gray-300">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
            <span>In Digistore24: <strong className="text-white">Konto &rarr; Einstellungen &rarr; IPN/Postback</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
            <span>IPN-URL eintragen:</span>
          </li>
        </ol>
        <CopyableUrl url="https://smartvoltacademy.de/api/digistore/ipn" />
        <ol start={3} className="space-y-3 text-sm text-gray-300 mt-3">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
            <span>IPN-Passwort: <code className="bg-gray-700 px-2 py-0.5 rounded text-yellow-400">Elektro567!!!</code></span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
            <span>Klicken Sie auf <strong className="text-white">"Testen"</strong>, um die Verbindung zu prüfen</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">5</span>
            <span>Diese Events aktivieren: <strong className="text-white">order_complete</strong>, <strong className="text-white">rebill</strong>, <strong className="text-white">refund</strong>, <strong className="text-white">chargeback</strong>, <strong className="text-white">cancel</strong></span>
          </li>
        </ol>
        <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-300">
          <strong>✓ Tipp:</strong> Endpoint ist bereits live. Prüfen Sie über <code className="bg-gray-900/60 px-1 rounded">/api/digistore/test-ipn</code>, ob die URL erreichbar ist.
        </div>
      </>
    )
  },
  {
    num: 4,
    title: 'Testen & Live schalten',
    icon: <RefreshCw className="w-5 h-5" />,
    content: (
      <>
        <p className="text-gray-300 mb-4">
          Bevor Sie live gehen, testen Sie den kompletten Kaufprozess:
        </p>
        <ol className="space-y-2 text-sm text-gray-300 list-decimal list-inside">
          <li>Erstellen Sie ein <strong className="text-white">Test-Produkt</strong> in Digistore24 mit 0€ Preis</li>
          <li>Kaufen Sie es selbst, um den IPN-Webhook zu testen</li>
          <li>Prüfen Sie, ob das Abo in der Plattform automatisch aktiviert wird</li>
          <li>Testen Sie den Zugang zu den freigeschalteten Kursen</li>
        </ol>
      </>
    )
  },
  {
    num: 5,
    title: 'Digistore24 Affiliate-Programm aktivieren',
    icon: <Users className="w-5 h-5" />,
    content: (
      <>
        <p className="text-gray-300 mb-4">
          Lassen Sie andere Ihre Pakete bewerben und teilen Sie eine Provision. Pro Verkauf via Affiliate-Link wird automatisch eine Provision gezahlt – komplett von Digistore24 abgewickelt.
        </p>
        <ol className="space-y-3 text-sm text-gray-300">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
            <span>In Digistore24: <strong className="text-white">Konto &rarr; Produkte &rarr; jedes Paket öffnen &rarr; Tab "Affiliates"</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
            <span>Aktivieren Sie <strong className="text-white">"Affiliate-Werbung erlaubt"</strong></span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
            <span>Provision setzen (Empfehlung: <code className="bg-gray-700 px-2 py-0.5 rounded text-yellow-400">30%</code> auf alle Abos und Rebills)</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
            <span>Affiliate-Vereinbarung & Werbematerialien (Banner, Texte, Cookies) hinterlegen</span>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-xs font-bold flex-shrink-0">5</span>
            <span>Im Marketplace listen lassen unter <strong className="text-white">Bildung / Handwerk</strong> – so finden Sie neue Affiliates</span>
          </li>
        </ol>
        <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-300">
          <strong>Wichtig:</strong> Die IPN-Aktivierung bleibt davon unberührt – Provisionen werden ausschließlich von Digistore24 verwaltet. Unsere Plattform schaltet Kurse über die IPN-Nachricht frei, unabhängig vom Affiliate.
        </div>
      </>
    )
  },
  {
    num: 6,
    title: 'Empfehlungs-Programm (50€ Gutschein)',
    icon: <Gift className="w-5 h-5" />,
    content: (
      <>
        <p className="text-gray-300 mb-4">
          Zusätzlich zum Affiliate-Programm läuft ein <strong className="text-white">internes Empfehlungs-Programm</strong>: Bestehende Kunden teilen ihren Link – beide Seiten erhalten 50&nbsp;€ Digistore24-Gutschein nach Erstbuchung.
        </p>
        <ul className="space-y-2 text-sm text-gray-300 list-disc list-inside">
          <li>Empfehlungs-Block wird automatisch auf der <code className="bg-gray-700 px-1 rounded">/danke</code>-Seite und in der Willkommens-E-Mail angezeigt</li>
          <li>Der eindeutige Link sieht so aus: <code className="bg-gray-700 px-1 rounded text-yellow-400">https://smartvoltacademy.de/register?ref=&lt;code&gt;</code></li>
          <li>Geworbene Betriebe sehen während der Registrierung einen 50€-Hinweis</li>
          <li>Erfolgreiche Empfehlungen werden in der Collection <code className="bg-gray-700 px-1 rounded">referrals</code> mit Status <code className="bg-gray-700 px-1 rounded">qualified</code> protokolliert</li>
        </ul>
        <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-300">
          <strong>So lösen Sie die Gutscheine ein:</strong> Listen Sie wöchentlich alle „qualified"-Einträge aus der DB und legen Sie in Digistore24 entsprechende Gutscheincodes an. Versenden Sie diese an die hinterlegten E-Mails (`referrer_email` + `referred_email`).
        </div>
      </>
    )
  }
];

const DigiStoreGuidePage = () => {
  const navigate = useNavigate();

  const readyFeatures = [
    'IPN-Webhook-Endpoint',
    'Automatische Abo-Aktivierung',
    'Willkommens-E-Mail nach Kauf',
    'Abo-Verlängerung bei Rebill',
    'Kündigung/Rückerstattung',
    'Zugangskontrolle (Abo-basiert)',
    '50% kostenloser Testkurs',
    'Empfehlungs-Programm (50€ Gutschein)',
    'Referral-Tracking in DB',
    'Cookie-Banner (DSGVO)',
    'SEO Meta-Tags'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-white">
      <Helmet>
        <title>Digistore24 Einrichtung - SmartVolt Academy</title>
      </Helmet>

      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto max-w-4xl px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-400 hover:text-white" data-testid="guide-back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <h1 className="text-lg font-bold text-yellow-500">Digistore24 Einrichtung</h1>
          <Button variant="outline" size="sm" onClick={() => window.open('https://www.digistore24.com', '_blank')} className="border-gray-600 text-gray-300 hover:text-white" data-testid="open-digistore-btn">
            <ExternalLink className="w-4 h-4 mr-1" /> Digistore24
          </Button>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-4 py-8 space-y-8">

        {/* Hero */}
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-yellow-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-yellow-500" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Zahlungen einrichten</h2>
          <p className="text-gray-400 max-w-lg mx-auto">
            In 6 Schritten verbinden Sie Ihre SmartVolt Academy mit Digistore24, aktivieren das Affiliate-Programm und das Empfehlungs-Programm.
          </p>
        </div>

        {/* Steps */}
        {steps.map((step) => (
          <Card key={step.num} className="bg-gray-900/50 border-gray-800" data-testid={`guide-step-${step.num}`}>
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center text-yellow-500">
                  {step.icon}
                </div>
                <div>
                  <Badge className="bg-gray-800 text-gray-400 border-0 mb-1">Schritt {step.num}</Badge>
                  <CardTitle className="text-white text-xl">{step.title}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>{step.content}</CardContent>
          </Card>
        ))}

        {/* Purchase flow */}
        <Card className="bg-gray-900/50 border-gray-800" data-testid="guide-flow">
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center text-green-500">
                <BookOpen className="w-5 h-5" />
              </div>
              <CardTitle className="text-white text-xl">So funktioniert der Kaufprozess</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Kunde klickt "Jetzt freischalten" auf der Website',
                'Weiterleitung zu Digistore24 Checkout',
                'Kunde bezahlt bei Digistore24',
                'Digistore24 sendet IPN an unsere Plattform',
                'Abo wird automatisch aktiviert + Willkommens-E-Mail',
                'Kunde hat sofort Zugang zu allen gekauften Kursen'
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                  <span className="text-gray-300 text-sm">{text}</span>
                  {i < 5 && <ArrowRight className="w-4 h-4 text-gray-600 ml-auto hidden sm:block" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Already configured */}
        <Card className="bg-gradient-to-r from-green-900/20 to-green-800/10 border-green-500/20" data-testid="guide-ready">
          <CardHeader>
            <CardTitle className="text-green-400 flex items-center gap-2">
              <Shield className="w-5 h-5" /> Bereits konfiguriert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {readyFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center py-6">
          <p className="text-gray-400 mb-4">Haben Sie Fragen? Kontaktieren Sie uns jederzeit.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => window.open('https://www.digistore24.com', '_blank')} className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold" data-testid="guide-start-btn">
              Jetzt bei Digistore24 einrichten <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/chef-cockpit')} className="border-gray-600 text-gray-300 hover:text-white">
              Zurück zum Chef-Cockpit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigiStoreGuidePage;
